import {useCallback, useEffect, useRef, useState} from 'react';
import {findDOMNode} from 'react-dom';
import screenfull from 'screenfull';

import VideoPlayerLayout from '../components/VideoPlayerLayout';

interface VideoPlayerProps {
  url: string;
  id: string;
  stoppageTime: number;
  durationInSeconds: number;
  onPreviousVideoClick: () => void;
  onNextVideoClick: () => void;
  isNextDisable: boolean;
  isPreviousDisable: boolean;
  onStopClick: () => void;
  onVideoFinish: () => void;
  setCurrentTime: (time: number) => void;
}

const VideoPlayer = ({
  url,
  stoppageTime,
  durationInSeconds,
  onPreviousVideoClick,
  onNextVideoClick,
  isNextDisable,
  isPreviousDisable,
  onStopClick,
  onVideoFinish,
  setCurrentTime,
}: VideoPlayerProps) => {
  const videoPlayerTime = useRef(0);
  const videoPlayerRef = useRef(null);
  const videoWithControlsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [played, setPlayed] = useState(stoppageTime);
  const [seeking, setSeeking] = useState(false);
  const [loaded, setLoaded] = useState(0);

  const handleSeekMouseDown = useCallback(() => {
    setSeeking(true);
  }, [seeking]);

  const handleSeekChange = useCallback(
    (event) => {
      setPlayed(parseFloat(event.target.value));
    },
    [played]
  );

  const handleSeekMouseUp = useCallback(
    (event) => {
      setSeeking(false);
      videoPlayerRef.current.seekTo(parseFloat(event.target.value));
    },
    [seeking]
  );

  const handleSetPlayingClick = useCallback(() => {
    // @ts-ignore
    onStopClick(playing);
    setPlaying(!playing);
  }, [playing]);

  const handleSetMutedClick = useCallback(() => {
    setMuted(!muted);
  }, [muted]);

  const handleProgressChange = useCallback(
    ({played, loaded}) => {
      if (!seeking) {
        setPlayed(played);
        setLoaded(loaded);
      }
    },
    [seeking]
  );

  useEffect(() => {
    videoPlayerTime.current = played;
    setCurrentTime(Math.round(played * durationInSeconds));
  }, [played]);

  const handleFullscreenClick = useCallback(() => {
    if (screenfull.isEnabled) {
      // eslint-disable-next-line react/no-find-dom-node
      screenfull.toggle(findDOMNode(videoWithControlsRef.current));
    }
  }, []);

  return (
    <VideoPlayerLayout
      url={`${url}#t=${stoppageTime}`}
      durationInSeconds={durationInSeconds}
      onFullScreenClick={handleFullscreenClick}
      onProgressChange={handleProgressChange}
      onSetPlayingClick={handleSetPlayingClick}
      onSetMutedClick={handleSetMutedClick}
      onSeekMouseUp={handleSeekMouseUp}
      onSeekChange={handleSeekChange}
      onSeekMouseDown={handleSeekMouseDown}
      loaded={loaded}
      played={played}
      isMuted={muted}
      isPlaying={playing}
      videoWithControlsRef={videoWithControlsRef}
      videoPlayerRef={videoPlayerRef}
      onNextVideoClick={onNextVideoClick}
      onPreviousVideoClick={onPreviousVideoClick}
      isNextDisable={isNextDisable}
      isPreviousDisable={isPreviousDisable}
      onVideoFinish={onVideoFinish}
    />
  );
};

export default VideoPlayer;
